import { useRoute, useRouter } from 'vue-router';
import { type Ref, ref, computed, watch } from 'vue';
import usePermission from '../../../hook/usePermission';
import type { MenuItem } from './types';
import { useSettingsStore, useUserStore } from '../..';
import { getMenuTreeAdapter } from './adapter';
import { defineStore } from 'pinia';
import { getDecodePathname } from '../../../utils/url';
import { getDefaultActive } from '../../../utils/menu';
import { activatedPath } from '../../../components/TabBar/state';

interface UseMenuStore {
  flatMenuObj: Ref<Record<string, MenuItem>>;
  menuTree: Ref<MenuItem[]>;
  menuPathMap: Ref<Record<string, MenuItem & { level: string }>>;
  loading: Ref<boolean>;
  sideMenus: Ref<MenuItem[]>;
  headerMenus: Ref<MenuItem[]>;
  activedIndex: Ref<number>;
  topHeaderHeight: Ref<string>;
  sideMenuWidth: Ref<string>;
  asideMenuOpen: Ref<boolean>;
  showSidebarMenu: Ref<boolean>;
  showHeaderMenu: Ref<boolean>;
  sideMenuWidthDefault: string;
  headerDefaultActive: Ref<Record<'activeId' | 'realActiveId', string>>;
  sidebarDefaultActive: Ref<Record<'activeId' | 'realActiveId', string>>;
  getMenuItemForUser: (key: string) => MenuItem | undefined;
  toggleAsideMenu: () => void;
  toggleAsideMenuForUser: (expand?: boolean) => void;
  getMenusData: () => Promise<void>;
  getMenusDataForUser: () => Promise<void>;
  setActived: (data: number | string) => void;
  setModuleIdToCache: (path: string) => void;
  updateMenus: (subAppName: string, menus: MenuItem[]) => void;
  topMenusPathMap: Ref<Record<string, string>>;
}

const useMenuStore = defineStore('menu', (): UseMenuStore => {
  /**
   * 完整的导航数据
   */
  const menuTree = ref<MenuItem[]>([]);
  const menuPathMap = ref<Record<string, MenuItem & { level: string }>>({});
  const loading = ref(true);
  const getMenusInitialized = ref(false);
  const permission = usePermission();
  const settingsStore = useSettingsStore();
  const userStore = useUserStore();
  const router = useRouter();
  const route = useRoute();

  const sideMenuWidthDefault = '200px';
  const sideMenuWidthMinDefault = '48px';

  /**
   * 菜单索引处理，支持多级菜单
   * @param items
   * @returns
   */
  const flatWorkbenchItems = (items: MenuItem[]): MenuItem[] => {
    const result: MenuItem[] = [];
    // 递归遍历每一项及其子项
    const addIndex = (menuItem: MenuItem, idx: string, parentPathName: string) => {
      const node = {
        ...menuItem,
        index: `${idx}`,
        parent: `${parentPathName}`,
      };
      // 找到匹配项
      result.push(node); // 仅添加子菜单项
      if (menuItem.children && menuItem.children.length > 0) {
        // 如果有子菜单，则继续递归
        menuItem.children?.forEach((childItem, cIdx) => {
          addIndex(childItem, `${idx}-${cIdx}`, `${node.parent ? `${node.parent}/` : ''}${menuItem.title}`);
        });
      }
    };
    // 开始遍历
    items.forEach((item, index) => {
      addIndex(item, index.toString(), '');
    });
    return result;
  };

  const flatMenuObj = computed(() => {
    const list = flatWorkbenchItems(menuTree.value) || [];
    return list.reduce(
      (obj, cur) => {
        // 兼容以前 find 逻辑，因为 find 是从前到后，查找到后就返回
        // 所以这里如果已经存在值，则不能用新值覆盖
        obj[cur.id] = obj[cur.id] || cur;
        obj[getDecodePathname(cur.path || '')] = obj[getDecodePathname(cur.path || '')] || cur;
        return obj;
      },
      {} as Record<string, MenuItem>,
    );
  });

  /**
   * !用户侧 api，请谨慎修改入参及返回
   * 获取当前菜单项
   * @param key route.path 或 id
   */
  const getMenuItemForUser = (key: string) => {
    return flatMenuObj.value[getDecodePathname(key)];
  };

  const filterSortMenus = (menus: MenuItem[] | undefined): MenuItem[] => {
    if (!menus || menus.length === 0) return [];

    return menus
      .filter((menuItem) => {
        if (!menuItem.permission) return true;
        return permission.checkPermission(menuItem.permission);
      })
      .map((menuItem) => {
        return {
          ...menuItem,
          children: filterSortMenus(menuItem.children),
        };
      })
      .sort(
        (a, b) =>
          (Number(a.order) - Number(b.order)) * (settingsStore.settings.menu.orderType === 'ascending-order' ? 1 : -1),
      );
  };

  const flattenTree = (tree: MenuItem[], parentLevel: string, parentIds: MenuItem['id'][]) => {
    const result: Record<string, any> = {};

    tree.forEach((item) => {
      const currentPath = item.path || '';
      const currentLevel = `${parentLevel ? `${parentLevel}-` : ''}${item.title}`;
      const currentId = [...parentIds, item.id];

      // 将当前节点添加到结果中
      result[currentPath] = { ...item, menuLevel: currentLevel, parentIds };
      result[item.id] = result[currentPath];

      // 如果有子节点，递归处理子节点
      if (item.children && item.children.length > 0) {
        const childrenResult = flattenTree(item.children, currentLevel, currentId);
        Object.assign(result, childrenResult);
      }
    });

    return result;
  };

  const getMenusData = async () => {
    getMenusInitialized.value = true;

    const baseOn = settingsStore.settings.menu.baseOn;
    let tree: MenuItem[] = [];
    let getMenuTreeFunc = getMenuTreeAdapter(baseOn);
    if (getMenuTreeFunc) {
      const { routes = [] } = router?.options || {};
      // await userStore.initUser(true);
      loading.value = true;
      tree = await getMenuTreeFunc(baseOn, routes, userStore.roleId);
      loading.value = false;
      menuTree.value = filterSortMenus(tree);
      menuPathMap.value = flattenTree(tree, '', []);

      // head-side 模式下设置主导航激活菜单
      if (settingsStore.settings.menu.menuMode === 'head-side') {
        // todo: 解决与 homePath 自动跳转时序问题，homePath 应该在这之前
        setActived(route?.path);
      }
    } else {
      loading.value = false;
    }
  };

  /**
   * !用户侧 api，请谨慎修改入参及返回
   */
  const getMenusDataForUser = getMenusData;

  /**
   * 侧边栏-二级及以下菜单不设置图标
   * @param menus
   * @param isReset
   */
  const resetItemIcon = (menus: MenuItem[] | undefined, isReset = true) => {
    if (!menus || menus.length === 0) return [];
    return menus.map((item: MenuItem): MenuItem => {
      return {
        ...item,
        icon: isReset ? '' : item.icon,
        // 这里控制二级及二级以下的菜单 icon 情况，通过 showIconInSubMenu 字段控制
        children: resetItemIcon(item.children, !settingsStore.settings.menu.showIconInSubMenu),
      };
    });
  };

  /**
   * 主导航菜单激活索引
   */
  const activedIndex = ref(-1);

  /**
   * 设置主导航
   */
  function setActived(data: number | string) {
    if (typeof data === 'number') {
      // 如果是 number 类型，则认为是主导航的索引
      activedIndex.value = data;
    } else {
      // 如果是 string 类型，则认为是路由，需要查找对应的主导航索引
      const findIndex = flatMenuObj.value[getDecodePathname(data)]?.index || '';

      if (findIndex) {
        activedIndex.value = parseInt(findIndex?.split('-')[0] || '0');
      } else {
        // 暴露兜底方法给用户自定义处理
        const { setActiveTopMenu } = settingsStore.settings?.menu;
        if (typeof setActiveTopMenu === 'function') {
          const newIndex = setActiveTopMenu(data, menuTree.value);
          if (typeof newIndex === 'number') {
            activedIndex.value = newIndex;
          }
        }
      }
    }
  }

  /**
   * 顶部导航菜单
   */
  const headerMenus = computed<MenuItem[]>(() => {
    if (menuTree.value.length === 0) return [];
    if (settingsStore.settings.menu.menuMode === 'side') return [];

    if (
      settingsStore.settings.menu.menuMode === 'head' ||
      settingsStore.settings.menu.showHeaderSubMenuInHeadSideMode
    ) {
      return resetItemIcon(menuTree.value as MenuItem[], false); // 顶部菜单栏-二级及以下菜单不设置图标;
    }

    const returnMenus: MenuItem[] = [];
    menuTree.value.forEach((menuItem, index) => {
      returnMenus.push({ ...menuItem } as MenuItem);
      returnMenus[index].children = [];
    });

    return resetItemIcon(returnMenus, false);
  });

  /**
   * 侧边栏导航菜单
   */
  const sideMenus = computed<MenuItem[]>(() => {
    if (menuTree.value.length === 0) return [];
    if (settingsStore.settings.menu.menuMode === 'head') return [];

    if (settingsStore.settings.menu.menuMode === 'side') {
      return resetItemIcon(menuTree.value as MenuItem[], false); // 侧边栏-二级及以下菜单不设置图标
    }

    return resetItemIcon((menuTree.value[activedIndex.value]?.children || []) as MenuItem[], false);
  });

  /**
   * 顶部header高度
   */
  const topHeaderHeight = computed(() => {
    return settingsStore.settings.layout.hiddenTopHeader ? '0px' : 'var(--top-nav-height)';
  });

  /**
   * 顶栏默认激活菜单
   */
  const headerDefaultActive = computed(() => {
    let target: MenuItem | undefined = headerMenus.value[activedIndex.value];
    if (settingsStore.settings.menu.menuMode === 'head-side' && target) {
      return { activeId: `${target.id}`, realActiveId: `${target.id}` };
    }

    target = getDefaultActive(headerMenus.value, route.path, null);

    if (!target) return { activeId: '', realActiveId: '' };

    let activeId = '';
    const { parentIds = [] } = menuPathMap.value[target.id] || { parentIds: [] };
    const { horizontalMenuShowLevel } = settingsStore.settings.menu;
    if (horizontalMenuShowLevel >= 4 && parentIds.length === horizontalMenuShowLevel - 1) {
      activeId = `${parentIds[parentIds.length - 1]}`; // 取父级 id
    } else {
      activeId = `${target.id}`;
    }

    return { activeId, realActiveId: `${target.id}` };
  });

  /**
   * 侧边栏默认激活菜单
   */
  const sidebarDefaultActive = computed(() => {
    const { setActiveSideMenu } = settingsStore.settings?.menu || {};
    const target = getDefaultActive(sideMenus.value, activatedPath.value, setActiveSideMenu);
    if (!target) {
      return { activeId: '', realActiveId: '' };
    }

    let activeId = '';
    const { parentIds = [] } = menuPathMap.value[target.id] || { parentIds: [] };
    const { horizontalMenuShowLevel } = settingsStore.settings.menu;
    if (horizontalMenuShowLevel >= 4 && parentIds.length === horizontalMenuShowLevel - 1) {
      activeId = `${parentIds[parentIds.length - 1]}`; // 取父级 id
    } else {
      activeId = `${target.id}`;
    }

    return { activeId, realActiveId: `${target.id}` };
  });

  /**
   * 侧边栏是否打开
   */
  const asideMenuOpen = ref(true);

  /**
   * 侧边栏是否显示
   */
  const showSidebarMenu = computed(() => {
    if (settingsStore.settings.menu.menuMode === 'head') {
      return false;
    }
    return !(settingsStore.settings.menu.menuMode === 'head-side' && sideMenus.value.length === 0);
  });

  /**
   * 侧边栏宽度
   */
  const sideMenuWidth = computed(() => {
    if (!showSidebarMenu.value) {
      return '0px';
    }
    return asideMenuOpen.value ? sideMenuWidthDefault : sideMenuWidthMinDefault;
  });

  /**
   * 顶部菜单栏是否显示
   */
  const showHeaderMenu = computed(() => {
    if (settingsStore.settings.menu.menuMode === 'side') {
      return false;
    }
    return !(settingsStore.settings.menu.menuMode === 'head-side' && headerMenus.value.length === 0);
  });

  /**
   * 打开关闭侧边栏
   */
  const toggleAsideMenu = () => {
    asideMenuOpen.value = !asideMenuOpen.value;
  };

  /**
   * !用户侧 api，请谨慎修改入参及返回
   * 切换侧边栏展开/收起
   * @param expand 是否展开侧边栏菜单，可不传，不传则根据当前状态进行判断
   */
  const toggleAsideMenuForUser = (expand?: boolean) => {
    asideMenuOpen.value = expand != null ? !!expand : !asideMenuOpen.value;
  };

  const topMenusPathMap = ref<Record<string, string>>({});
  const menusPath2TopIdMap = computed(() => {
    const flat: Record<string, string | number> = {};
    const setFlatMenu = (topId: string | number, arr: MenuItem[]) => {
      arr.forEach((n) => {
        if (n.children?.length) {
          setFlatMenu(topId, n.children);
          return;
        }
        flat[n.path || ''] = topId;
      });
    };
    menuTree.value.forEach((v) => {
      setFlatMenu(v.id, v?.children || []);
    });

    return flat;
  });

  const getPathByChildren = (v: MenuItem) => {
    const { children, path } = v;
    if (children?.length) {
      const p = children.find((item) => !item.isLink && !item.hideInMenu);
      if (p) {
        return getPathByChildren(p);
      } else {
        return path || '';
      }
    }
    return path || '';
  };

  const updatetopMenusPathMapByRoute = () => {
    const v = route?.fullPath;
    const id = menusPath2TopIdMap.value[v];
    if (!id) return;
    topMenusPathMap.value[id] = v;
  };

  watch(
    headerMenus,
    (v) => {
      const obj: Record<string, string> = {};
      v.forEach((n, i) => {
        obj[n.id] = getPathByChildren(menuTree.value[i]);
      });
      topMenusPathMap.value = obj;
      updatetopMenusPathMapByRoute();
    },
    { immediate: true },
  );

  watch(route, updatetopMenusPathMapByRoute, { immediate: true });

  // 监听切换菜单数据来源、排序变化、用户角色，重新获取菜单数据
  watch(
    [() => settingsStore.settings.menu.baseOn, () => settingsStore.settings.menu.orderType],
    () => {
      getMenusData();
    },
    { deep: true },
  );

  watch(
    [() => userStore.roleData, () => userStore.roleCode],
    (cur, prev) => {
      const { shouldRefreshMenuOnRoleListChange, shouldRefreshMenuOnRoleChange } = settingsStore.settings.menu;
      if (
        (cur[0] !== prev[0] && shouldRefreshMenuOnRoleListChange) ||
        (cur[1] !== prev[1] && shouldRefreshMenuOnRoleChange)
      ) {
        getMenusData();
        return;
      }

      if (!getMenusInitialized.value) {
        getMenusData();
        return;
      }
    },
    { deep: true },
  );

  /**
   * 设置 moduleId 到缓存中（大件中会用到这个 id）
   * @param path 当前路径
   */
  const setModuleIdToCache = (path: string) => {
    const menuTarget = menuPathMap.value[path];
    if (menuTarget) {
      window.sessionStorage.setItem('moduleId', `${menuTarget.id || ''}`);
    }
  };

  watch(
    () => route?.path,
    (v) => {
      setModuleIdToCache(v);
    },
  );

  /**
   * 子应用加载完成后调用，更新其模块下的菜单数据。
   * 递归查找 menuTree 中 name 匹配的节点，替换其 children。
   * flatMenuObj、sideMenus、headerMenus 等计算属性自动响应刷新。
   *
   * @param subAppName - 子应用标识，对应 subApps 配置中的 name
   * @param menus - 菜单数据，与初始化 getMenus 返回的 MenuItem[] 结构一致
   */
  const updateMenus = (subAppName: string, menus: MenuItem[]) => {
    if (!menus?.length) return;

    const findAndUpdate = (tree: MenuItem[]): boolean => {
      for (const node of tree) {
        if (node.name === subAppName) {
          node.children = filterSortMenus(menus);
          return true;
        }
        if (node.children?.length && findAndUpdate(node.children)) {
          return true;
        }
      }
      return false;
    };

    const found = findAndUpdate(menuTree.value);
    if (!found) {
      console.warn(`[o-layouts] updateMenus: 未找到 name="${subAppName}" 的菜单节点`);
      return;
    }

    menuPathMap.value = flattenTree(menuTree.value, '', []);
  };

  return {
    flatMenuObj,
    topMenusPathMap,
    menuTree: menuTree as Ref<MenuItem[]>,
    menuPathMap: menuPathMap as Ref<Record<string, MenuItem & { level: string }>>,
    loading,
    sideMenus,
    headerMenus,
    activedIndex,
    topHeaderHeight,
    sideMenuWidth,
    asideMenuOpen,
    showSidebarMenu,
    showHeaderMenu,
    sideMenuWidthDefault,
    headerDefaultActive,
    sidebarDefaultActive,
    getMenuItemForUser,
    toggleAsideMenu,
    toggleAsideMenuForUser,
    getMenusData,
    getMenusDataForUser,
    setActived,
    setModuleIdToCache,
    updateMenus,
  };
});

export default useMenuStore;
